import { useEffect, useMemo, useRef, useState } from 'react';
import { Search, MapPin, Clock, X } from 'lucide-react';

const norm = (s) => (s || '').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
const RECENT_KEY = 'photo_address_recent';
const readRecent = () => { try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); } catch { return []; } };
export const rememberAddress = (clientName, address) => {
  try {
    const list = readRecent().filter((r) => !(r.clientName === clientName && r.address === address));
    localStorage.setItem(RECENT_KEY, JSON.stringify([{ clientName, address, at: Date.now() }, ...list].slice(0, 6)));
  } catch { /* noop */ }
};

/** Zoekbare keuzelijst over alle klanten + adressen: typ naam, adres of plaats. Toont label/naam, adres en bedrijf. */
export const AddressPicker = ({ clients, clientName, address, onChange, disabled, placeholder = 'Zoek op naam, adres of plaats…', testId = 'address-picker' }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [hi, setHi] = useState(0);
  const boxRef = useRef();
  const inputRef = useRef();

  const options = useMemo(() => {
    const out = [];
    (clients || []).forEach((c) => {
      const addrs = c.addresses || [];
      if (addrs.length === 0) out.push({ key: `${c.id}-none`, clientName: c.name, address: '', label: '', city: '', text: norm(c.name) });
      addrs.forEach((a) => {
        const addr = a.address || a.street || '';
        const city = addr.split(',').slice(-1)[0]?.trim() || '';
        out.push({ key: a.id || `${c.id}-${addr}`, clientName: c.name, address: addr, label: a.label || '', city, isPrimary: a.is_primary, text: norm(`${c.name} ${a.label || ''} ${addr}`) });
      });
    });
    return out;
  }, [clients]);

  const words = norm(q).split(/\s+/).filter(Boolean);
  const filtered = words.length ? options.filter((o) => words.every((w) => o.text.includes(w))) : options;
  const recent = !words.length ? readRecent().map((r) => options.find((o) => o.clientName === r.clientName && o.address === r.address)).filter(Boolean) : [];
  const list = [...recent.map((o) => ({ ...o, recent: true })), ...filtered.filter((o) => !recent.includes(o))].slice(0, 60);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  useEffect(() => { setHi(0); }, [q, open]);

  const pick = (o) => { onChange({ clientName: o.clientName, address: o.address, label: o.label }); setQ(''); setOpen(false); };
  const selected = options.find((o) => o.clientName === clientName && (o.address === address || (!o.address && !address)));
  const summary = clientName ? `${selected?.label ? `${selected.label} · ` : ''}${address || '(geen adres)'} — ${clientName}` : '';

  const onKey = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) { setOpen(true); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setHi((h) => Math.min(h + 1, list.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    if (e.key === 'Enter' && list[hi]) { e.preventDefault(); pick(list[hi]); }
    if (e.key === 'Escape') setOpen(false);
  };

  return (
    <div ref={boxRef} className="relative" data-testid={testId}>
      {clientName && !open ? (
        <button type="button" disabled={disabled} onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 0); }}
          className="w-full flex items-center gap-2 text-left bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-sm text-gray-800 hover:border-[#3da5a5]" data-testid={`${testId}-selected`}>
          <MapPin className="w-4 h-4 text-[#3da5a5] shrink-0" />
          <span className="truncate flex-1">{summary}</span>
          <span role="button" tabIndex={-1} onClick={(e) => { e.stopPropagation(); onChange({ clientName: '', address: '', label: '' }); }} className="text-gray-400 hover:text-red-500" title="Wissen" data-testid={`${testId}-clear`}><X className="w-4 h-4" /></span>
        </button>
      ) : (
        <div className="relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input ref={inputRef} value={q} disabled={disabled} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onKeyDown={onKey}
            placeholder={placeholder} className="w-full bg-gray-50 border border-gray-200 rounded-md pl-9 pr-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#3da5a5]" data-testid={`${testId}-input`} autoComplete="off" />
        </div>
      )}
      {open && (
        <div className="absolute z-50 mt-1 w-full max-h-80 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-xl" data-testid={`${testId}-list`}>
          {list.length === 0 && <p className="px-3 py-3 text-sm text-gray-400">Niets gevonden voor "{q}"</p>}
          {list.map((o, i) => (
            <button type="button" key={`${o.key}-${o.recent ? 'r' : 'a'}`} onMouseDown={(e) => e.preventDefault()} onClick={() => pick(o)} onMouseEnter={() => setHi(i)}
              className={`w-full text-left px-3 py-2 flex items-start gap-2 border-b border-gray-50 last:border-0 ${i === hi ? 'bg-[#3da5a5]/10' : ''}`} data-testid={`${testId}-option-${i}`}>
              {o.recent ? <Clock className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" title="Recent gebruikt" /> : <MapPin className="w-4 h-4 text-gray-300 mt-0.5 shrink-0" />}
              <span className="min-w-0 flex-1">
                <span className="block text-sm text-gray-800 truncate"><strong>{o.label || o.clientName}</strong>{o.address ? <span className="text-gray-700"> · {o.address}</span> : <span className="text-gray-400"> · geen adres — vul handmatig in</span>}</span>
                <span className="block text-[11px] text-gray-500 truncate">{o.label ? `${o.clientName}` : ''}{o.label && o.city ? ' · ' : ''}{o.isPrimary ? ' · hoofdadres' : ''}{o.recent ? ' · recent' : ''}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
